import { Flex, Modal } from "@mantine/core";
import { DateInput, TimePicker } from "@mantine/dates";
import type { IAccessPopulated } from "@mcapps/shared";
import React from "react";

import { IMeeting } from "@mctypes/meeting.types";
import { useHistory } from "react-router-dom";
import { FailedAlert, McTitle } from "../../../../components";
import { AccessComp } from "../../../../components/AccessComp/AccessComp";
import { ColorDropdown } from "../../../../components/ColorDropDown";
import McDropdownNew from "../../../../components/McDropdown/MCDropdownNew";
import McInputNew from "../../../../components/McInput/McInputNew";
import McTextAreaNew from "../../../../components/McInput/McTextAreaNew";
import { useAppSelector } from "../../../../hooks";
import { getProjectIdFromUrl } from "../../../utils";
import { useGetProjectByProjectIdIssueboardQuery } from "../../../../../openapi";
import {
  MEETING_INTERVALS,
  MEETINGS_SERIES_STATUSES,
} from "../../projectdashboard/MeetingSeries/meetingSeriesConstants";
import { useUpdateMeetingSerieItemMutation } from "../meetingSerieRTK";

const toDateInputValue = (value?: string | null): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toYyyyMmDd = (value: Date | string | null): string => {
  if (!value) return "";
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getIdString = (value: unknown): string | null => {
  if (value == null) return null;
  if (typeof value === "string") return value;
  if (typeof value === "object" && "_id" in value) {
    const id = (value as { _id?: unknown })._id;
    return id != null ? String(id) : null;
  }
  return String(value);
};

export const MeetingSerieItemModal: React.FC<{
  meetingSerie: IMeeting.MeetingSerie;
  toggle: () => void;
}> = ({ meetingSerie, toggle }) => {
  const [updateMeetingSerie, { isError: updateError }] = useUpdateMeetingSerieItemMutation();
  const history = useHistory();
  const projectId = getProjectIdFromUrl(history.location.pathname);

  const { data: issueBoards = [] } = useGetProjectByProjectIdIssueboardQuery({
    projectId: projectId,
  });
  const members = useAppSelector((state) => state.adminReducer.project.members);
  const loggedInUserId = useAppSelector((state) => state.globalReducer.user?._id);
  const users = React.useMemo(
    () =>
      members.reduce((acc, member) => {
        if (member.user != null) {
          acc.push(member.user);
        }
        return acc;
      }, []),
    [members],
  );
  const [accessEditMode, setAccessEditMode] = React.useState(false);
  const loggedInMember = React.useMemo(
    () => members.find((member) => getIdString(member.user) === loggedInUserId),
    [loggedInUserId, members],
  );
  const canManageAccess =
    loggedInUserId != null &&
    (loggedInMember?.group === "ADMIN" || getIdString(meetingSerie.createdBy) === loggedInUserId);

  React.useEffect(() => {
    if (canManageAccess === false) {
      setAccessEditMode(false);
    }
  }, [canManageAccess]);

  const access = React.useMemo<IAccessPopulated>(() => {
    const meetingSerieAccess = meetingSerie?.access;
    return {
      level: meetingSerieAccess?.level ?? "PROJECT",
      groups: meetingSerieAccess?.groups ?? [],
      users: (meetingSerieAccess?.users ?? []).map((user) => {
        if (typeof user !== "string") {
          return user;
        }

        const projectUser = users.find((projectUser) => projectUser._id === user);
        return {
          _id: user,
          name: projectUser?.name ?? "",
        };
      }),
    };
  }, [meetingSerie?.access, users]);

  const onChange: IMeeting.onMeetingSerieItemChange = (oldValue, newValue, param) => {
    updateMeetingSerie({
      projectId: projectId,
      meetingSerieId: meetingSerie._id,
      attribute: param,
      value: newValue,
    });
  };

  return (
    <Modal opened onClose={toggle} title={meetingSerie?.name}>
      {updateError === true && <FailedAlert />}
      {meetingSerie != null && (
        <Flex direction="column" gap="xs">
          <McTitle title="Status">
            <McDropdownNew
              currentValue={MEETINGS_SERIES_STATUSES.find((item) => item.value === meetingSerie.status)}
              displayKey="label"
              options={MEETINGS_SERIES_STATUSES}
              onChange={(oldVal, newObj) => onChange(null, newObj.value as any, "status")}
            />
          </McTitle>
          <McTitle title="Tittel">
            <McInputNew onBlur={(e) => onChange(meetingSerie.name, e.target.value, "name")} value={meetingSerie.name} />
          </McTitle>
          <McTitle title="Beskrivelse">
            <McTextAreaNew
              spellCheck={false}
              autosize
              onBlur={(e) => onChange(meetingSerie.description, e.target.value, "description")}
              value={meetingSerie.description}
            />
          </McTitle>
          <McTitle title="Interval">
            <McDropdownNew
              currentValue={MEETING_INTERVALS.find((item) => item.id === String(meetingSerie.interval))}
              displayKey="name"
              options={MEETING_INTERVALS}
              onChange={(oldVal, newObj) => onChange(null, newObj.id, "interval")}
            />
          </McTitle>
          <McTitle title="Startdato">
            <DateInput
              styles={{ input: { minHeight: "2.625rem", height: "2.625rem" } }}
              valueFormat="DD.MM.YYYY"
              value={toDateInputValue(meetingSerie.seriesStart)}
              onChange={(value) => {
                onChange(meetingSerie.seriesStart, toYyyyMmDd(value), "seriesStart");
              }}
            />
          </McTitle>
          <McTitle title="Til dato">
            <DateInput
              styles={{ input: { minHeight: "2.625rem", height: "2.625rem" } }}
              clearable
              valueFormat="DD.MM.YYYY"
              value={toDateInputValue(meetingSerie.seriesEnd)}
              onChange={(value) => {
                onChange(meetingSerie.seriesEnd, toYyyyMmDd(value), "seriesEnd");
              }}
            />
          </McTitle>
          <McTitle title="Starttid">
            <TimePicker
              styles={{ input: { minHeight: "2.625rem", height: "2.625rem" } }}
              format="24h"
              withSeconds={false}
              withDropdown
              value={meetingSerie.timeStart ?? ""}
              onChange={(value) => {
                if ((meetingSerie.timeStart ?? "") === value) return;
                onChange(meetingSerie.timeStart, value, "timeStart");
              }}
            />
          </McTitle>
          <McTitle title="Sluttid">
            <TimePicker
              styles={{ input: { minHeight: "2.625rem", height: "2.625rem" } }}
              format="24h"
              withSeconds={false}
              withDropdown
              value={meetingSerie.timeEnd ?? ""}
              onChange={(value) => {
                if ((meetingSerie.timeEnd ?? "") === value) return;
                onChange(meetingSerie.timeEnd, value, "timeEnd");
              }}
            />
          </McTitle>
          <McTitle title="Farge">
            <ColorDropdown
              activeColor={meetingSerie.color}
              setColor={(color) => onChange(meetingSerie.color, color, "color")}
            />
          </McTitle>
          <McTitle title="Standard saksregister">
            <McDropdownNew
              disabled={issueBoards.length === 0}
              onClear={
                meetingSerie.defaultIssueBoard != null
                  ? () => onChange(meetingSerie.defaultIssueBoard as any, null as any, "defaultIssueBoard")
                  : undefined
              }
              currentValue={meetingSerie.defaultIssueBoard}
              onChange={(oldVal, newVal) => onChange(null, newVal, "defaultIssueBoard")}
              options={issueBoards.map((board) => ({ name: board.name, _id: board._id })) ?? []}
              displayKey="name"
            />
          </McTitle>
          {canManageAccess === true && (
            <McTitle title="Tilgangsstyring">
              <AccessComp
                access={access}
                isEditable
                variant="md"
                fontSize="1.1em"
                updateAccess={(access) =>
                  onChange(meetingSerie.access, {
                    level: access.level,
                    groups: access.groups,
                    users: access.users.map((user) => user._id),
                  } as any, "access")
                }
                editMode={accessEditMode}
                allUsers={users}
                editCb={(bool) => setAccessEditMode(bool)}
              />
            </McTitle>
          )}
        </Flex>
      )}
    </Modal>
  );
};

export default MeetingSerieItemModal;
